/**
 * Helpers that convert demo-song cells to FamiTracker-style display strings.
 *
 * Format conventions (mirrors FT2 / FamiTracker):
 *   - Empty cell:    "--- -- --"
 *   - Note on:       "C-5 D2 C0"  (note  duty  volume in hex)
 *   - Note off:      "OFF -- --"
 *   - Volume hex:    00..F0 in two chars; we use uppercase
 *   - Duty letters:  D0=12.5%, D1=25%, D2=50%, D3=75%
 *
 * These are pure functions, easily unit-tested. The PatternGrid renders
 * the strings without caring about their structure.
 */

import type { DemoCell, DemoDPCMCell, DemoNoiseCell } from '../audio/demo-song';

/** Two-character hex padded with leading zero, uppercase. */
function hex2(v: number): string {
  return v.toString(16).toUpperCase().padStart(2, '0');
}

/** Empty placeholder of the right width for an unset cell. */
export const EMPTY_CELL = '---';
export const EMPTY_SUB = '--';

export function formatPulseCell(cell: DemoCell | undefined): string {
  if (!cell || cell.note == null) return `${EMPTY_CELL} ${EMPTY_SUB} ${EMPTY_SUB}`;
  if (cell.note === 'OFF') return `OFF ${EMPTY_SUB} ${EMPTY_SUB}`;
  const duty = cell.duty !== undefined ? `D${cell.duty}` : EMPTY_SUB;
  const vol = cell.volume !== undefined ? hex2(cell.volume) : EMPTY_SUB;
  return `${cell.note} ${duty} ${vol}`;
}

export function formatTriangleCell(cell: DemoCell | undefined): string {
  if (!cell || cell.note == null) return EMPTY_CELL;
  if (cell.note === 'OFF') return 'OFF';
  return cell.note;
}

export function formatNoiseCell(cell: DemoNoiseCell | undefined): string {
  if (!cell || cell.note == null) return `${EMPTY_CELL} ${EMPTY_SUB} ${EMPTY_SUB}`;
  if (cell.note === 'OFF') return `OFF ${EMPTY_SUB} ${EMPTY_SUB}`;
  const period = cell.periodIndex !== undefined ? `P${cell.periodIndex.toString().padStart(2, '0')}` : EMPTY_SUB;
  const vol = cell.volume !== undefined ? hex2(cell.volume) : EMPTY_SUB;
  return `${cell.mode === 'short' ? 'sht' : 'lng'} ${period} ${vol}`;
}

export function formatDPCMCell(cell: DemoDPCMCell | undefined): string {
  if (!cell || !cell.trigger) return `${EMPTY_CELL} ${EMPTY_SUB}`;
  const name = cell.trigger.toUpperCase().slice(0, 4).padEnd(4, ' ');
  const rate = cell.rateIndex !== undefined ? cell.rateIndex.toString().padStart(2, '0') : EMPTY_SUB;
  return `${name}${rate}`;
}

export interface ChannelSpec {
  /** Display label in the column header. */
  label: string;
  /** Tailwind class for the column color accent. */
  colorClass: string;
  /** Width hint in `ch` units so columns line up under a monospace font. */
  widthCh: number;
}

export const CHANNEL_SPECS: Record<'pulse1' | 'pulse2' | 'triangle' | 'noise' | 'dpcm', ChannelSpec> = {
  pulse1: { label: 'Pulse 1', colorClass: 'text-nes-cyan', widthCh: 11 },
  pulse2: { label: 'Pulse 2', colorClass: 'text-nes-cyan/80', widthCh: 11 },
  triangle: { label: 'Tri', colorClass: 'text-nes-white', widthCh: 4 },
  noise: { label: 'Noise', colorClass: 'text-nes-red', widthCh: 13 },
  dpcm: { label: 'DPCM', colorClass: 'text-nes-cyan/90', widthCh: 8 },
};
